import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchPublicApps, getSeedApps } from '../services/appsService';
import { sortApps } from '../utils/appSchema';

/**
 * Danh sách ứng dụng công khai cho trang chủ và trang giới thiệu.
 * Hiển thị seed ngay lập tức rồi thay bằng dữ liệu Firebase khi tải xong,
 * nên người dùng không phải nhìn màn hình trắng.
 */
export const useApps = () => {
    const [apps, setApps] = useState(() => sortApps(getSeedApps().filter((app) => !app.hidden)));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        fetchPublicApps()
            .then((list) => {
                if (active && list.length) setApps(list);
            })
            .catch((err) => active && setError(err))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, []);

    const categories = useMemo(
        () => ['all', ...Array.from(new Set(apps.map((app) => app.category))).sort()],
        [apps],
    );

    /**
     * Thay một bản ghi vừa được sửa tại chỗ (xem EditModeGate) để danh sách đổi
     * ngay mà không phải tải lại trang. Mục vừa bị ẩn thì biến mất khỏi danh
     * sách công khai — đúng như những gì khách sẽ thấy.
     */
    const upsertApp = useCallback((saved) => {
        if (!saved?.id) return;
        setApps((prev) => {
            const rest = prev.filter((app) => app.id !== saved.id);
            return sortApps(saved.hidden ? rest : [...rest, saved]);
        });
    }, []);

    return { apps, categories, loading, error, upsertApp };
};

/** Tìm một ứng dụng theo id, kèm danh sách gợi ý cùng danh mục. */
export const useApp = (id) => {
    const { apps, loading, upsertApp } = useApps();
    const app = useMemo(() => apps.find((item) => item.id === id) || null, [apps, id]);
    const related = useMemo(() => {
        if (!app) return [];
        return apps
            .filter((item) => item.id !== app.id && item.category === app.category)
            .slice(0, 3);
    }, [apps, app]);

    return { app, related, loading, upsertApp };
};
